import { useRef } from "react";
import { GoChevronRight, GoChevronLeft } from "react-icons/go";

const DRAG_THRESHOLD = 8;

const Slider = ({ children, className = "" }) => {
  const sliderRef = useRef(null);
  const drag = useRef({
    pointerId: null,
    axis: null,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    moved: false,
  });
  const suppressClick = useRef(false);

  const onPointerDown = (e) => {
    if (!sliderRef.current) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (e.target.closest("button, input, textarea, select, a")) return;

    drag.current = {
      pointerId: e.pointerId,
      axis: null,
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: sliderRef.current.scrollLeft,
      moved: false,
    };
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (d.pointerId !== e.pointerId || !sliderRef.current) return;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!d.axis) {
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      d.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (d.axis === "x") {
        sliderRef.current.classList.add("active");
        sliderRef.current.setPointerCapture?.(e.pointerId);
      } else {
        drag.current.pointerId = null;
      }
    }

    if (d.axis !== "x") return;
    d.moved = true;
    e.preventDefault();
    sliderRef.current.scrollLeft = d.scrollLeft - dx;
  };

  const onPointerUp = (e) => {
    if (drag.current.pointerId !== e.pointerId) return;
    if (drag.current.moved) suppressClick.current = true;
    drag.current.pointerId = null;
    drag.current.axis = null;
    drag.current.moved = false;
    sliderRef.current?.classList.remove("active");
  };

  const onClickCapture = (e) => {
    if (!suppressClick.current) return;
    e.preventDefault();
    e.stopPropagation();
    suppressClick.current = false;
  };

  const scrollByTrack = (dir) => {
    if (!sliderRef.current) return;
    sliderRef.current.scrollBy({
      left: dir * sliderRef.current.offsetWidth * 0.7,
      behavior: "smooth",
    });
  };

  return (
    <div dir="rtl" className={`g-slider ${className}`.trim()}>
      <div
        className="g-slider-track"
        ref={sliderRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={onClickCapture}
      >
        {children}
      </div>

      <button
        type="button"
        className="g-slider-btn prev"
        onClick={() => scrollByTrack(-1)}
        aria-label="السابق"
      >
        <GoChevronLeft />
      </button>
      <button
        type="button"
        className="g-slider-btn next"
        onClick={() => scrollByTrack(1)}
        aria-label="التالي"
      >
        <GoChevronRight />
      </button>

      <style>{`
        .g-slider {
          position: relative;
          margin: 0;
          padding: 0 52px;
        }
        .g-slider-track {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          overflow-x: auto;
          overflow-y: visible;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          touch-action: pan-x pan-y;
          overscroll-behavior-x: contain;
          padding: 10px 4px 14px;
          cursor: grab;
        }
        .g-slider-track::-webkit-scrollbar {
          display: none;
        }
        .g-slider-track.active {
          cursor: grabbing;
          scroll-behavior: auto;
        }
        .g-slider-track > * {
          flex: 0 0 auto;
        }
        .g-slider-track > .product-card {
          flex: 0 0 250px;
          width: 250px;
          max-width: 250px;
        }
        .g-slider-btn {
          position: absolute;
          top: 46%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 50%;
          background: var(--accent, #C9A227);
          color: var(--on-accent, #0a0a0a);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          z-index: 3;
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
        }
        .g-slider-btn.prev { left: 0; }
        .g-slider-btn.next { right: 0; }
        .g-slider-btn svg {
          width: 22px;
          height: 22px;
        }
        @media (max-width: 767px) {
          .g-slider {
            padding: 0;
          }
          .g-slider-btn {
            display: none;
          }
          .g-slider-track {
            gap: 0.7rem;
            padding: 6px 2px 12px;
            scroll-snap-type: x mandatory;
            cursor: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default Slider;
